/**
 * Environment access — single point of truth.
 * Reads VITE_-prefixed vars and falls back to safe defaults.
 * Never import import.meta.env elsewhere; import from here.
 *
 * Production fail-closed:
 *   When the bundle was built with `vite build` (PROD), the app must NOT
 *   silently fall back to localStorage mock auth. If the Supabase envs are
 *   missing in a production build, we throw at module load so the misdeploy
 *   surfaces immediately rather than shipping a weaker auth path.
 *   Dev/preview builds keep the mock fallback so the prototype boots offline.
 *
 * Auth split (same pattern as HOTTR.men / HOTTR.tv):
 *   - App data: VITE_SUPABASE_* (destination project; RLS + profiles)
 *   - Passport identity: VITE_PASSPORT_* + passport.hottr.world SSO
 *   - Soft-launch PIN: VITE_PIN_ACCESS_CODE / PIN_ACCESS_CODE (client gate only)
 */

const read = (key: string): string => {
  const envBag = (
    import.meta as ImportMeta & { env: Record<string, string | boolean | undefined> }
  ).env;
  const value = envBag?.[key];
  return typeof value === 'string' ? value.trim() : '';
};

const pinFromDefine =
  typeof __PIN_ACCESS_CODE__ !== 'undefined'
    ? String(__PIN_ACCESS_CODE__ || '').trim()
    : '';

const supabaseUrl = read('VITE_SUPABASE_URL');
const supabaseAnonKey = read('VITE_SUPABASE_ANON_KEY');
const pinAccessCode =
  pinFromDefine || read('VITE_PIN_ACCESS_CODE') || read('PIN_ACCESS_CODE');

const passportSupabaseUrl =
  read('VITE_PASSPORT_SUPABASE_URL') ||
  'https://xsbqsfrfkdhhcscnalqk.supabase.co';
const passportSupabaseAnonKey = read('VITE_PASSPORT_SUPABASE_ANON_KEY');
const passportUrl = read('VITE_PASSPORT_URL') || 'https://passport.hottr.world';

if (import.meta.env.PROD && (!supabaseUrl || !supabaseAnonKey)) {
  // Hard-fail the bundle. Better a visible crash than a silent mock-mode
  // deploy in production. Surfaces immediately in Netlify/Sentry.
  throw new Error(
    '[hottr] Production build is missing VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY. ' +
      'Refusing to boot in mock mode. Check the Netlify environment variables.',
  );
}

export const env = {
  supabaseUrl,
  supabaseAnonKey,
  surface: read('VITE_HOTTR_SURFACE') || 'hottr.app',
  mapboxToken: read('VITE_MAPBOX_TOKEN'),
  mapboxStyle: read('VITE_MAPBOX_STYLE') || 'mapbox://styles/mapbox/dark-v11',

  /** Passport auth project + public SSO host */
  passportSupabaseUrl,
  passportSupabaseAnonKey,
  passportUrl,

  /** Soft-launch PIN (client-side only; empty = gate disabled) */
  pinAccessCode,

  // Persona — selfie verification. Optional; feature is hidden when missing.
  personaTemplateId: read('VITE_PERSONA_TEMPLATE_ID'),
  personaEnv: read('VITE_PERSONA_ENV'), // 'sandbox' | 'production'

  // Stripe — billing. Optional; PlansPage is hidden when missing.
  stripePublishableKey: read('VITE_STRIPE_PUBLISHABLE_KEY'),
  stripePremiumPriceId: read('VITE_STRIPE_PREMIUM_PRICE_ID'),
  stripeVipPriceId: read('VITE_STRIPE_VIP_PRICE_ID'),
  stripeBoostSinglePriceId: read('VITE_STRIPE_BOOST_SINGLE_PRICE_ID'),
  stripeBoostPackPriceId: read('VITE_STRIPE_BOOST_PACK_PRICE_ID'),

  // Web Push — VAPID. Optional; push toggle is hidden when missing.
  vapidPublicKey: read('VITE_VAPID_PUBLIC_KEY'),

  // Observability — optional error-beacon collector (Sentry DSN or any URL).
  // When absent, observability falls back to console only.
  sentryDsn: read('VITE_SENTRY_DSN'),

  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,
} as const;

export const hasSupabase = (): boolean =>
  Boolean(env.supabaseUrl && env.supabaseAnonKey);

export const hasPassportAuth = (): boolean =>
  Boolean(env.passportSupabaseUrl && env.passportSupabaseAnonKey);

export const hasPinGate = (): boolean => env.pinAccessCode.length > 0;

export const hasMapbox = (): boolean => Boolean(env.mapboxToken);

export const hasPersona = (): boolean => Boolean(env.personaTemplateId);
export const hasStripe = (): boolean => Boolean(env.stripePublishableKey);
export const hasPush = (): boolean => Boolean(env.vapidPublicKey);

declare const __PIN_ACCESS_CODE__: string | undefined;
